import { useMemo, useState, useRef, MutableRefObject } from 'react';
import { css } from '@emotion/react';
import { Reorder, useDragControls, motion, VariantLabels } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import type { ColumnInstance, IdType, TableInstance } from 'react-table';
import { IconButton, usePopoverDialog, PopoverDialog, CheckboxField, useFormField, Text, TextLink } from '../..';
import { useThemeValues } from '../../../hooks';
import { ColumnsIcon, HamburgerMenuIcon } from '../../../icon';
import { TableCommonStyles } from '../common-styles';
import { TableData } from '../table-data-type';
import { getColumnIdsWithStickyOrder, isReservedCellId, persistUserPreference } from '../utils';

type FilterColumnsContainerProps<T extends TableData> = {
  tableInstance: TableInstance<T>;
  isFilterEnabled?: boolean;
  onChange?: (hiddenColumns: string[]) => void;
};

export const FilterColumnsContainer = <T extends TableData>({
  tableInstance,
  isFilterEnabled,
  onChange,
}: FilterColumnsContainerProps<T>) => {
  const {
    state: { hiddenColumns },
    filterColumnsConfig,
    tableTrackingIds,
  } = tableInstance;
  const notificationBadgeStyles = TableCommonStyles.filterNotificationBadgeStyles;
  const { borderRadius, spacing } = useThemeValues();
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({
    placement: 'bottom-end',
    initialOffset: { x: -290, y: 40 },
  });

  const notificationStyle = () => [
    notificationBadgeStyles,
    css`
      ::after {
        opacity: ${hiddenColumns?.length ? 1 : 0};
      }
    `,
  ];

  return (
    <section
      css={[
        css`
          position: relative;
        `,
        isFilterEnabled &&
          css`
            margin-left: ${spacing(1)};
          `,
      ]}
    >
      <IconButton
        showLabelOnHover
        disabled={tableInstance.isLoading}
        css={notificationStyle()}
        color='light'
        trackingId={tableTrackingIds.filterColumns}
        data-test-id={tableTrackingIds.filterColumns}
        {...getTriggerProps()}
        label={filterColumnsConfig?.label ?? 'Filter Columns'}
      >
        <ColumnsIcon />
      </IconButton>
      <PopoverDialog
        {...getDialogProps()}
        css={css`
          width: 330px;
          border-radius: ${borderRadius.medium};
          padding: ${spacing(1, 0)};
        `}
      >
        <ColumnFilters tableInstance={tableInstance} onChange={onChange} />
      </PopoverDialog>
    </section>
  );
};

type ColumnFilters<T extends TableData> = {
  tableInstance: TableInstance<T>;
  onChange?: (hiddenColumns: string[]) => void;
};

export const ColumnFilters = <T extends TableData>({ tableInstance, onChange }: ColumnFilters<T>) => {
  const filterColumnStyle = TableCommonStyles.filterColumnStyles;

  const {
    allColumns,
    toggleHideAllColumns,
    visibleColumns,
    state: { hiddenColumns },
    uniqueFilterColumnsId,
    uniqueOrderColumnsId,
    filterColumnsConfig,
    setColumnOrder,
  } = tableInstance;

  const nonActionColumns = useMemo(() => {
    return allColumns.filter((column) => !isReservedCellId(column.id)).length;
  }, [allColumns.length]);

  const nonActionVisibleColumns = useMemo(() => {
    return visibleColumns.filter((column) => !isReservedCellId(column.id)).length;
  }, [visibleColumns.length]);

  const columnsIds = allColumns.map((column) => column.id);

  const [columnList, setColumnList] = useState(columnsIds);
  const [isDragging, setIsDragging] = useState(false);
  const constraintRef = useRef<HTMLUListElement | null>(null);

  return (
    <motion.article
      initial={false}
      layout='position'
      transition={{
        layout: {
          duration: 0,
        },
      }}
      layoutScroll
      css={css`
        width: 100%;
      `}
    >
      <section css={filterColumnStyle}>
        <Text weight='semibold' color='light'>
          {filterColumnsConfig?.label ?? 'Filter Columns'}
        </Text>
        <TextLink
          disabled={nonActionVisibleColumns === nonActionColumns}
          weight='semibold'
          onClick={() => {
            toggleHideAllColumns(false);
            onChange?.([]);
            window.localStorage.removeItem(uniqueFilterColumnsId ?? '');
          }}
        >
          {filterColumnsConfig?.showAllLabel ?? 'Show All'}
        </TextLink>
      </section>
      <Reorder.Group
        transition={{
          layout: {
            duration: 0,
          },
        }}
        initial={false}
        layout='position'
        css={css`
          position: relative;
          padding: 0;
        `}
        axis='y'
        ref={constraintRef}
        values={columnList}
        onReorder={setColumnList}
      >
        {columnList.map((listColumnId) => (
          <ColumnFilterItem
            allColumns={allColumns}
            columnList={columnList}
            hiddenColumns={hiddenColumns}
            listColumnId={listColumnId}
            setColumnOrder={setColumnOrder}
            uniqueFilterColumnsId={uniqueFilterColumnsId}
            uniqueOrderColumnsId={uniqueOrderColumnsId}
            constraintRef={constraintRef}
            setIsDragging={setIsDragging}
            isDragging={isDragging}
            key={listColumnId}
            onChange={onChange}
          />
        ))}
      </Reorder.Group>
    </motion.article>
  );
};

type ColumnFilterItem<T extends TableData> = {
  allColumns: ColumnInstance<T>[];
  columnList: IdType<T>[];
  hiddenColumns: IdType<T>[] | undefined;
  listColumnId: IdType<T>;
  setColumnOrder: TableInstance<T>['setColumnOrder'];
  uniqueFilterColumnsId: string | undefined;
  uniqueOrderColumnsId: string | undefined;
  constraintRef: MutableRefObject<HTMLUListElement | null>;
  setIsDragging: (isDragging: boolean) => void;
  isDragging: boolean;
  onChange?: (hiddenColumns: string[]) => void;
};

export const ColumnFilterItem = <T extends TableData>({
  listColumnId,
  setColumnOrder,
  allColumns,
  uniqueOrderColumnsId,
  uniqueFilterColumnsId,
  columnList,
  hiddenColumns,
  constraintRef,
  setIsDragging,
  isDragging,
  onChange,
}: ColumnFilterItem<T>) => {
  const { spacing, colors } = useThemeValues();
  const reorderColumnWhileTapStyles = TableCommonStyles.reorderColumnWhileTapStyles;

  const { Header, headerLabel, disableColumnFilter, toggleHidden, id, isVisible } = allColumns.find(
    (column) => column.id === listColumnId
  ) as ColumnInstance<T>;
  const dragControls = useDragControls();
  const fieldProps = useFormField({ type: 'checkbox', value: isVisible }, [isVisible]);

  if ((typeof Header === 'function' && !headerLabel) || isReservedCellId(id) || disableColumnFilter) return null;

  const onDragEnd = () => {
    setColumnOrder(getColumnIdsWithStickyOrder(columnList, allColumns));
    persistUserPreference(uniqueOrderColumnsId, columnList);
    setIsDragging(false);
  };

  return (
    <Reorder.Item
      initial={false}
      key={listColumnId}
      id={listColumnId}
      value={listColumnId}
      dragListener={false}
      dragElastic={0.05}
      as={'ul'}
      dragConstraints={constraintRef}
      dragControls={dragControls}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={onDragEnd}
      whileTap={reorderColumnWhileTapStyles as unknown as VariantLabels}
      style={{
        position: 'relative',
        minHeight: spacing(5),
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center',
        padding: spacing(1, 2),
        background: colors.white,
      }}
    >
      <HamburgerMenuIcon
        size={20}
        color='light'
        onPointerDown={(event) => dragControls.start(event)}
        css={css`
          margin-right: ${spacing(1.5)};
          cursor: ${isDragging ? 'grabbing' : 'grab'};
        `}
      />

      <CheckboxField
        {...fieldProps}
        name={headerLabel ?? onlyText(Header as React.ReactNode)}
        label={headerLabel ?? onlyText(Header as React.ReactNode)}
        labelPlacement='left'
        onChange={() => {
          if (!hiddenColumns || !uniqueFilterColumnsId) return;

          toggleHidden(fieldProps.value);

          const hiddenColumnsCurrent = fieldProps.value
            ? [...hiddenColumns, id]
            : hiddenColumns.filter((hiddenColumn) => hiddenColumn !== id);

          if (!hiddenColumnsCurrent.length) {
            window.localStorage.removeItem(uniqueFilterColumnsId);
          }

          persistUserPreference(uniqueFilterColumnsId, hiddenColumnsCurrent);
          onChange?.(hiddenColumnsCurrent);
        }}
        css={css`
          width: 100%;
          display: flex;
          justify-content: space-between;
        `}
      />
    </Reorder.Item>
  );
};
